/**
 * IMPORT CSV MODAL
 * Modale principale d'import CSV en 4 étapes
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  toast,
} from '../ui';
import type {
  CSVColumnMapping,
  CSVImportConfig,
  CSVImportMode,
  CSVImportResult,
  CSVParsedData,
} from '../../types/csv';
import { ImportStepUpload } from './ImportStepUpload';
import { ImportStepMapping } from './ImportStepMapping';
import { ImportStepPreview } from './ImportStepPreview';
import { ImportStepResult } from './ImportStepResult';
import { suggestColumnMapping, importCSVData } from '../../lib/csv-import';

type Step = 'upload' | 'mapping' | 'preview' | 'import' | 'result';

interface ImportCSVModalProps<T> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: CSVImportConfig<T>;
  mode: CSVImportMode;
  onCreate: (data: Partial<T>) => Promise<T | boolean | null>;
  onUpdate?: (id: string, data: Partial<T>) => Promise<boolean>;
  existingData?: T[];
  entityName: string;
  entityNamePlural: string;
  onSuccess: (result: CSVImportResult<T>) => void;
}

export function ImportCSVModal<T extends { id?: string }>({
  open,
  onOpenChange,
  config,
  mode,
  onCreate,
  onUpdate,
  existingData = [],
  // entityName,
  entityNamePlural,
  onSuccess,
}: ImportCSVModalProps<T>) {
  const [currentStep, setCurrentStep] = useState<Step>('upload');
  const [csvData, setCsvData] = useState<CSVParsedData | null>(null);
  const [mapping, setMapping] = useState<CSVColumnMapping[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(
    null
  );
  const [importResult, setImportResult] = useState<CSVImportResult<T> | null>(null);

  // Reset state when modal closes
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isImporting) {
        // Reset
        setCurrentStep('upload');
        setCsvData(null);
        setMapping([]);
        setImportProgress(null);
        setImportResult(null);
      }
      onOpenChange(newOpen);
    },
    [onOpenChange, isImporting]
  );

  // Étape 1 : Upload
  const handleFileLoaded = useCallback(
    (data: CSVParsedData) => {
      setCsvData(data);

      // Générer le mapping automatique
      const suggestedMapping = suggestColumnMapping(data.headers, config);

      // Ajouter les previews
      const mappingWithPreview = suggestedMapping.map((m) => ({
        ...m,
        preview: data.rows.slice(0, 3).map((row) => row[m.csvColumn] || ''),
      }));

      setMapping(mappingWithPreview);
      setCurrentStep('mapping');
      toast.success(`${data.rowCount} lignes détectées`);
    },
    [config]
  );

  const handleUploadError = useCallback((error: string) => {
    toast.error(error);
  }, []);

  // Étape 2 : Mapping
  const canProceedToPreview = useMemo(() => {
    // Tous les champs obligatoires doivent être mappés
    return config.requiredFields.every((field) => mapping.some((m) => m.dbField === String(field)));
  }, [mapping, config.requiredFields]);

  // Étape 4 : Import
  const handleStartImport = useCallback(async () => {
    if (!csvData) return;

    setCurrentStep('import');
    setIsImporting(true);
    setImportProgress({ current: 0, total: csvData.rowCount });

    try {
      const result = await importCSVData(
        csvData.rows,
        mapping,
        config,
        mode,
        onCreate,
        onUpdate,
        existingData,
        (current, total) => {
          setImportProgress({ current, total });
        }
      );

      setImportResult(result);
      setCurrentStep('result');

      // Le toast est géré par le parent (useEntityList.handleImportSuccess)
      // pour éviter les doublons
      onSuccess(result);
    } catch (error) {
      console.error("Erreur lors de l'import:", error);
      toast.error(error instanceof Error ? error.message : "Erreur lors de l'import");
      setCurrentStep('preview');
    } finally {
      setIsImporting(false);
      setImportProgress(null);
    }
  }, [csvData, mapping, config, mode, onCreate, onUpdate, existingData, onSuccess]);

  // Titres et boutons par étape
  const stepConfig = {
    upload: {
      title: `Importer des ${entityNamePlural}`,
      showBack: false,
      showNext: false,
      nextLabel: undefined,
      nextDisabled: false,
      showCancel: true,
    },
    mapping: {
      title: 'Correspondance des colonnes',
      showBack: true,
      showNext: true,
      nextLabel: 'Aperçu',
      nextDisabled: !canProceedToPreview,
      showCancel: true,
    },
    preview: {
      title: 'Aperçu des données',
      showBack: true,
      showNext: true,
      nextLabel: `Importer ${csvData?.rowCount || 0} lignes`,
      nextDisabled: false,
      showCancel: true,
    },
    import: {
      title: 'Import en cours',
      showBack: false,
      showNext: false,
      nextLabel: undefined,
      nextDisabled: false,
      showCancel: false,
    },
    result: {
      title: "Résultat de l'import",
      showBack: false,
      showNext: false,
      nextLabel: undefined,
      nextDisabled: false,
      showCancel: false,
    },
  };

  const currentConfig = stepConfig[currentStep];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="!max-w-[80vw] !w-[80vw] !max-h-[90vh] !h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl">{currentConfig.title}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-6 px-2">
          {currentStep === 'upload' && (
            <ImportStepUpload onFileLoaded={handleFileLoaded} onError={handleUploadError} />
          )}

          {currentStep === 'mapping' && csvData && (
            <ImportStepMapping
              mapping={mapping}
              config={config}
              onMappingChange={setMapping}
              csvData={csvData}
            />
          )}

          {currentStep === 'preview' && csvData && (
            <ImportStepPreview csvData={csvData} mapping={mapping} config={config} />
          )}

          {(currentStep === 'import' || currentStep === 'result') && (
            <ImportStepResult
              result={importResult}
              isImporting={isImporting}
              progress={importProgress}
              entityNamePlural={entityNamePlural}
            />
          )}
        </div>

        <DialogFooter>
          {currentConfig.showCancel && (
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isImporting}
            >
              Annuler
            </Button>
          )}

          {currentConfig.showBack && (
            <Button
              variant="outline"
              onClick={() => {
                if (currentStep === 'mapping') setCurrentStep('upload');
                if (currentStep === 'preview') setCurrentStep('mapping');
              }}
            >
              Retour
            </Button>
          )}

          {currentConfig.showNext && (
            <Button
              onClick={() => {
                if (currentStep === 'mapping') setCurrentStep('preview');
                if (currentStep === 'preview') handleStartImport();
              }}
              disabled={currentConfig.nextDisabled}
            >
              {currentConfig.nextLabel || 'Suivant'}
            </Button>
          )}

          {currentStep === 'result' && (
            <Button onClick={() => handleOpenChange(false)}>Fermer</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
